import React, { createContext, useMemo, ReactNode } from 'react';
import { UserPublicConsentKeysForEventDocument, useSetUserConsentForEventMutation, useUserPublicConsentKeysForEventQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { UserConsent, UserConsentKey } from './useUserConsent.types';
import { useEventInfo } from '@shared/utils/eventInfo';

export const UserConsentContext = createContext<UserConsent | undefined>(undefined);

export const UserConsentProvider = ({ children }: { children: ReactNode }) => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const shouldSkip = !eventId;

  const { data, loading: loadingUserConsent } = useUserPublicConsentKeysForEventQuery({
    variables: { eventId: eventId! },
    batchMode: 'fast',
    skip: shouldSkip,
  });

  const [updateUserConsent] = useSetUserConsentForEventMutation();

  const consentMap = useMemo(() => {
    const entries: ReadonlyArray<[UserConsentKey, boolean]> = data?.userPublicConsentKeysForEvent?.map(item => [item.consentKey as UserConsentKey, item.consentAnswer]) ?? [];
    return new Map<UserConsentKey, boolean>(entries);
  }, [data]);

  const getUserConsent = useEventCallback((consentKey: UserConsentKey): boolean | undefined => {
    return consentMap.get(consentKey);
  });

  const setUserConsent = useEventCallback(async (consentKey: UserConsentKey, consentAnswer: boolean) => {
    if (!eventId) return;

    await updateUserConsent({
      variables: {
        data: {
          eventId,
          consentKey,
          consentAnswer,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: UserPublicConsentKeysForEventDocument,
          variables: {
            eventId,
          },
        },
      ],
    });
  });

  return <UserConsentContext.Provider value={{ getUserConsent, setUserConsent, loadingUserConsent }}>{children}</UserConsentContext.Provider>;
};
